import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ASSETS, SERVICES, COMPANY_INFO } from '../config/assets';
import { CheckCircle, Sparkles, Shield, Clock, Award, ArrowRight, Crown, Diamond, Star, Play, Mail, Phone, MapPin, Send, ChevronLeft, ChevronRight, Share } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';

const PolishedConcretePage = () => {
  const service = SERVICES.find(s => s.slug === 'polished-concrete');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: 'Mechanically Polished Concrete'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // All MPC project images from the previous website
  const allMPCImages = [
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210925_153556-scaled.jpg', title: 'Premium MPC Installation', description: 'High-gloss mechanically polished concrete with mirror-like finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_125521-scaled.jpg', title: 'Commercial Space Polish', description: 'Large commercial area with premium MPC finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_125523-scaled.jpg', title: 'Reflective Surface Detail', description: 'Close-up of mirror-like polished concrete surface' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_125527-scaled.jpg', title: 'Professional Equipment', description: 'Diamond polishing equipment creating premium finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_125533-scaled.jpg', title: 'Progress Documentation', description: 'Mid-process polishing work showing technique' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_130042-scaled.jpg', title: 'Aggregate Exposure', description: 'Full aggregate exposure with high-gloss polish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_130051-scaled.jpg', title: 'Final Polish Stage', description: 'Final polishing stage achieving mirror reflection' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211205_135746-scaled.jpg', title: 'Retail Space MPC', description: 'Premium retail installation with authentic MPC' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211205_154321-scaled.jpg', title: 'Commercial Polish Complete', description: 'Completed commercial mechanically polished project' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220302_235731-scaled.jpg', title: 'Light Reflectivity Demo', description: 'Demonstrating 90% light reflectivity capabilities' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220303_025441-scaled.jpg', title: 'Precision Polishing Work', description: 'Detailed polishing technique and craftsmanship' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220304_010208-scaled.jpg', title: 'Densifier Application', description: 'Professional densifying process for MPC' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220304_010444-scaled.jpg', title: 'Equipment Detail View', description: 'Professional polishing machinery in operation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220304_041155-scaled.jpg', title: 'Quality Control Check', description: 'Surface inspection and quality verification' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220304_041748-scaled.jpg', title: 'Polishing Progress', description: 'Step-by-step polishing progression documentation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220304_041804-scaled.jpg', title: 'Surface Preparation', description: 'Initial grinding before polishing process' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220419_154021-scaled.jpg', title: 'Residential MPC Project', description: 'Luxury home polished concrete installation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220419_154855-scaled.jpg', title: 'Before & After Comparison', description: 'Dramatic transformation through MPC process' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220419_164244-scaled.jpg', title: 'Finished Surface Quality', description: 'Completed MPC showing superior finish quality' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220419_234820-scaled.jpg', title: 'High-Gloss Achievement', description: 'Maximum polish level with mirror-like reflection' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220422_032031-scaled.jpg', title: 'Large Area Coverage', description: 'Extensive commercial space MPC installation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220926_151441-scaled.jpg', title: 'Premium Finish Detail', description: 'Close-up of authentic mechanically polished surface' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220927_154240-scaled.jpg', title: 'Professional Installation', description: 'Expert team completing MPC installation' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220928_070813-scaled.jpg', title: 'Surface Reflection Test', description: 'Testing light reflection and surface quality' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20230104_062427-scaled.jpg', title: 'Modern Office Installation', description: 'Contemporary office space with MPC flooring' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20230104_064635-scaled.jpg', title: 'Complete Project View', description: 'Finished MPC project showing overall results' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allMPCImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allMPCImages.length) % allMPCImages.length);
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', phone: '', message: '', serviceType: 'Mechanically Polished Concrete' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const premiumFeatures = [
    { icon: Crown, title: 'Premium Organic Process', description: 'The most authentic form of polished concrete using natural diamond grinding' },
    { icon: Diamond, title: 'Real Polished Concrete', description: 'Not just a coating - this is genuine mechanically polished concrete' },
    { icon: Star, title: 'Class & Sophistication', description: 'The premium choice for discerning clients who demand the very best' },
    { icon: Shield, title: 'Superior Durability', description: 'Outlasts all alternatives including grind and seal by decades' }
  ];

  const prestigeClients = [
    'Big-name department stores and retail chains',
    'Luxury residential properties and penthouses', 
    'Premium automotive showrooms',
    'Exclusive art galleries and museums',
    'Five-star hotel lobbies and restaurants',
    'Corporate headquarters and executive offices'
  ];

  const mpcAdvantages = [
    { title: 'Authentic Process', description: 'True mechanical polishing, not chemical treatments or coatings' },
    { title: 'Unmatched Longevity', description: '10+ year lifespan vs. 3-5 years for grind and seal alternatives' },
    { title: 'Light Reflectivity', description: 'Up to 90% light reflection - dramatically brightens any space' },
    { title: 'Low Maintenance', description: 'No resealing, waxing, or ongoing chemical treatments required' },
    { title: 'Eco-Certified', description: 'LEED points contributor with zero VOC emissions' },
    { title: 'Investment Grade', description: 'Increases property value significantly more than alternatives' }
  ];

  // Sample YouTube video data - you can update these with actual video IDs
  const youtubeVideos = [
    {
      id: 'bXYB4-kEhfA', // Actual video from your channel
      title: 'MPC Installation: Residential Space',
      description: 'MPC - Basement, Roseville.'
    },
    {
      id: 'oxvGc3Td9GI', // Actual video from your channel
      title: 'Commercial MPC: Existing Concrete Project',
      description: 'MPC - Civic Centre, Camden.'
    },
    {
      id: 'Xz-PZdWdRWE', // Actual video from your channel
      title: 'Residential MPC: Nil Exposure Finish',
      description: 'MPC - Award Winning Home, Coogee Beach.'
    }
  ];

  // Sample YouTube Shorts data - you can update these with actual video IDs
  const youtubeShorts = [
    {
      id: 'D59W5WMjBnw', // Actual short from your channel
      title: 'MPC Garage Polishing',
      description: 'Whale Beach MPC process in under 60 seconds'
    },
    {
      id: 'hy7FbYQTikQ', // Actual short from your channel
      title: 'The Doltone House',
      description: 'Prestigious venue MPC transformation'
    },
    {
      id: 'nCHTZNQgK10', // Actual short from your channel
      title: 'Mascot Office',
      description: '600sqm commercial MPC office flooring showcase'
    },
    {
      id: 'LmBgGRaP960', // Actual short from your channel
      title: 'MPC Mirror Finish',
      description: 'Professional crack repair & MPC mirror polish'
    },
    {
      id: 'gz4l2_eyris', // Actual short from your channel
      title: 'Premium MPC Quality',
      description: 'Residential premium basement polishing project'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="polished-concrete-hero-video"
        >
          <source src={service.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
              Premium Service
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Mechanically
              <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent block">
                Polished Concrete
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              The premium, organic form of polished concrete. Real mechanical polishing for discerning clients who demand authentic luxury.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0 shadow-2xl"
              asChild
            >
              <Link to="/contact" data-testid="hero-cta-button">
                Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              The Ultimate in Authentic Concrete Polishing
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Mechanically Polished Concrete (MPC) represents the pinnacle of concrete floor finishing. This is not a coating, 
              not a treatment, but the genuine organic form of polished concrete. Using advanced diamond grinding technology, 
              we transform ordinary concrete into a stunning, mirror-like surface that rivals natural stone.
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Chosen by major big-name department stores and exclusive residential properties worldwide, MPC delivers 
              unmatched sophistication and class. This is <em>real</em> polished concrete - far superior to cost-effective 
              alternatives like grind and seal.
            </p>
          </div>
        </motion.section>

        {/* Premium Features */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Why MPC is the Premium Choice
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {premiumFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950">
                  <CardContent className="p-6 text-center">
                    <feature.icon className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                    <h4 className="font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Prestige Applications */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Trusted by Premium Brands
              </h3>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                When the world's most prestigious retailers and luxury residential developers choose a flooring solution, 
                they choose Mechanically Polished Concrete. It's the organic, authentic choice that delivers unparalleled 
                beauty and performance.
              </p>
              <div className="grid gap-3">
                {prestigeClients.map((client, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Crown className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200">{client}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Card className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border-slate-300 dark:border-slate-600">
                <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                  MPC vs. Alternatives
                </h4>
                <div className="space-y-4">
                  <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <h5 className="font-medium text-green-800 dark:text-green-200 mb-1">Mechanically Polished Concrete</h5>
                    <p className="text-sm text-green-700 dark:text-green-300">✓ 10+ year lifespan ✓ Low maintenance ✓ Authentic process</p>
                  </div>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <h5 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">Grind & Seal (Budget Option)</h5>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">⚠ 3-5 year lifespan ⚠ Requires resealing ⚠ Coating-based</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* MPC Advantages */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            MPC Technical Advantages
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mpcAdvantages.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {advantage.title}
                      </h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {advantage.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* MPC Project Gallery Carousel */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            MPC Project Gallery
          </h3>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <div className="relative">
                {/* Main Image Display */}
                <div className="aspect-video relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={allMPCImages[currentImageIndex].src}
                    alt={allMPCImages[currentImageIndex].title}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    data-testid="mpc-carousel-main-image"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="mpc-carousel-prev-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="mpc-carousel-next-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {allMPCImages.length}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950">
                  <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {allMPCImages[currentImageIndex].title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {allMPCImages[currentImageIndex].description}
                  </p>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 bg-slate-100 dark:bg-slate-800">
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {allMPCImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === currentImageIndex 
                            ? 'border-amber-500 dark:border-amber-400' 
                            : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                        data-testid={`mpc-thumbnail-${index}`}
                      >
                        <img
                          src={image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Carousel Controls */}
            <div className="text-center mt-6">
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                {allMPCImages.length} premium mechanically polished concrete projects showcasing authentic quality
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={prevImage}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextImage}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* YouTube Videos */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Watch MPC in Action
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid={`youtube-video-${index}`}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {video.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-videos-button"
            >
              View All Videos on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* YouTube Shorts */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Quick MPC Insights - YouTube Shorts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {youtubeShorts.map((short, index) => (
              <motion.div
                key={short.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-[9/16]">
                    <iframe
                      src={`https://www.youtube.com/embed/${short.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={short.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                    {/* Shorts Label */}
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs rounded font-medium pointer-events-none z-10">
                      Shorts
                    </div>
                    {/* Share arrow (white) */}
                    <div className="absolute top-3 right-3 text-white pointer-events-none z-10">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                      </svg>
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {short.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {short.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-shorts-button"
            >
              View All Shorts on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
              Request Your MPC Consultation
            </h3>
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="mpc-contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-200">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 dark:text-slate-200">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+614 XX XXX XXX"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 dark:text-slate-200">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your MPC project - space type, size, timeline, and any specific requirements..."
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-textarea"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send MPC Inquiry <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
              
              {/* Contact Information */}
              <div className="space-y-8">
                <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950 border-amber-200 dark:border-amber-800">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    Premium MPC Specialists
                  </h4>
                  <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                    As leading mechanically polished concrete specialists, we deliver the same premium 
                    quality trusted by international retailers and luxury residential developers.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-amber-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-amber-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-amber-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.address}</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    What to Expect
                  </h4>
                  <div className="space-y-3 text-slate-600 dark:text-slate-300">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Free on-site consultation and assessment</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Detailed project proposal with timeline</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Transparent pricing with no hidden costs</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Premium workmanship guarantee</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
            <Crown className="w-16 h-16 text-amber-400 dark:text-amber-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold mb-4">
              Experience the MPC Difference
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Don't settle for alternatives. Choose the authentic, organic form of polished concrete 
              that delivers unmatched beauty, durability, and prestige for your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0"
                onClick={() => document.querySelector('[data-testid="mpc-contact-form"]').scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-contact-button"
              >
                Get Your MPC Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                asChild
              >
                <Link to="/portfolio" data-testid="final-cta-portfolio-button">
                  View Premium Projects
                </Link>
              </Button>
            </div>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default PolishedConcretePage;